import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, Building2, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { invalidateEntitlements } from '../../../hooks/useEntitlements';
import { apiRequest } from '../../../utils/apiClient';
import { StatusChip } from '../../../components/ui/StatusChip';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

type ActivityType =
  | 'BROKER' | 'DEVELOPER' | 'INVESTOR' | 'CONSULTANT'
  | 'ANALYST' | 'APPRAISER' | 'PROPERTY_MANAGEMENT' | 'FACILITIES_MANAGEMENT';
type ActivityStatus =
  | 'DECLARED' | 'VERIFYING' | 'VERIFIED' | 'EXPIRING_SOON' | 'EXPIRED' | 'SUSPENDED' | 'REJECTED';

interface AccountActivity {
  id: string;
  activityType: ActivityType;
  status: ActivityStatus;
  userId?: string | null;
  companyId?: string | null;
  licenseNumber?: string | null;
  professionalProfile?: { displayName?: string | null } | null;
}
interface ActivitiesResponse {
  user: AccountActivity[];
  company: AccountActivity[];
  companyId: string | null;
  companyRole: string | null;
}

// INVESTOR is intentionally NOT a licensed-role option: an investor is a capital
// role with no FAL licence to verify, so it has no place in this picker. People
// invest via the Investors Hub, not by adding a "licensed activity".
const PRO_TYPES: ActivityType[] = [
  'BROKER', 'DEVELOPER', 'CONSULTANT',
  'ANALYST', 'APPRAISER', 'PROPERTY_MANAGEMENT', 'FACILITIES_MANAGEMENT',
];

export default function ProfessionalAccountPage() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const isRTL = i18n.language === 'ar';
  const token = session?.token ?? null;

  const [data, setData] = useState<ActivitiesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [addType, setAddType] = useState<ActivityType | ''>('');
  const [addScope, setAddScope] = useState<'user' | 'company'>('user');
  const [displayName, setDisplayName] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');

  const typeLabel = (type: ActivityType) =>
    t(`pro.type.${type}`, {
      defaultValue: {
        BROKER: 'Broker & Marketer', DEVELOPER: 'Developer', INVESTOR: 'Investor',
        CONSULTANT: 'Consultant', ANALYST: 'Analyst', APPRAISER: 'Appraiser',
        PROPERTY_MANAGEMENT: 'Property Management', FACILITIES_MANAGEMENT: 'Facilities Management',
      }[type],
    });

  const load = async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const res = await apiRequest<ActivitiesResponse>('/account/activities', { token });
      setData(res);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 404
        ? t('pro.disabled', { defaultValue: 'Professional roles are not enabled yet.' })
        : t('pro.loadError', { defaultValue: 'Could not load your professional roles.' }));
    } finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, [token]);

  const addRole = async () => {
    if (!addType || !token || busy) return;
    setBusy(true); setError(null);
    try {
      const body: Record<string, unknown> = { activityType: addType, scope: addScope };
      if (displayName) body.profile = { displayName };
      const res = await apiRequest<{ activity: AccountActivity }>('/account/activities', { method: 'POST', token, body });
      if (licenseNumber && res?.activity?.id) {
        await apiRequest(`/account/activities/${res.activity.id}/verify-license`, {
          method: 'POST', token, body: { licenseNumber },
        }).catch(() => undefined);
      }
      setAddType(''); setDisplayName(''); setLicenseNumber('');
      // A new role can make this account professional: drop the cached access
      // answer so the Requests board unlocks now, not after the 60 s cache.
      invalidateEntitlements();
      await load();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409
        ? t('pro.dupRole', { defaultValue: 'You already hold this role.' })
        : t('pro.addError', { defaultValue: 'Could not add the role.' }));
    } finally { setBusy(false); }
  };

  const registerCompany = async () => {
    if (!companyName || !token || busy) return;
    setBusy(true); setError(null);
    try {
      await apiRequest('/account/company', {
        method: 'POST', token, body: { name: companyName, phone: companyPhone || undefined },
      });
      setCompanyName(''); setCompanyPhone('');
      // Owning a company counts as professional too (see addRole).
      invalidateEntitlements();
      await load();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409
        ? t('pro.hasCompany', { defaultValue: 'You already belong to a company.' })
        : t('pro.companyError', { defaultValue: 'Could not register the company.' }));
    } finally { setBusy(false); }
  };

  const activityRow = (a: AccountActivity) => (
    <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl">
      <div className="flex items-center gap-3 min-w-0">
        <span className="shrink-0 w-9 h-9 rounded-lg bg-violet-50 dark:bg-violet-900/30 grid place-items-center text-violet-600 dark:text-violet-300">
          <Briefcase className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{typeLabel(a.activityType)}</div>
          {a.professionalProfile?.displayName ? (
            <div className="text-xs text-gray-500 truncate">{a.professionalProfile.displayName}</div>
          ) : null}
        </div>
      </div>
      <StatusChip status={a.status} />
    </div>
  );

  const inputCls = 'w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500/30 transition-colors';
  const hasCompany = !!data?.companyId;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
      <Navigation />
      <div className="max-w-3xl mx-auto py-8 px-4">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-gray-100">
            {t('pro.title', { defaultValue: 'Grow your account' })}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('pro.subtitle', { defaultValue: 'Add a licensed professional role to yourself, or register a company you own. Each role is verified by its licence.' })}
          </p>
        </header>

        {error ? (
          <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current roles */}
            {(data && (data.user.length > 0 || data.company.length > 0)) ? (
              <section>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  {t('pro.yourRoles', { defaultValue: 'Your professional roles' })}
                </h2>
                <div className="space-y-2">
                  {data.user.map(activityRow)}
                  {data.company.map(activityRow)}
                </div>
              </section>
            ) : null}

            {/* Add a role */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
              <h2 className="font-semibold text-[var(--pv-color-primary)] dark:text-gray-100 mb-4 flex items-center gap-2">
                <Plus className="w-4 h-4 text-violet-600" /> {t('pro.addRole', { defaultValue: 'Add a professional role' })}
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <select className={inputCls} value={addType} onChange={(e) => setAddType(e.target.value as ActivityType)}>
                  <option value="">{t('pro.selectRole', { defaultValue: 'Select a role…' })}</option>
                  {PRO_TYPES.map((tp) => <option key={tp} value={tp}>{typeLabel(tp)}</option>)}
                </select>
                {hasCompany ? (
                  <select className={inputCls} value={addScope} onChange={(e) => setAddScope(e.target.value as 'user' | 'company')}>
                    <option value="user">{t('pro.scopeUser', { defaultValue: 'For me (individual)' })}</option>
                    <option value="company">{t('pro.scopeCompany', { defaultValue: 'For my company' })}</option>
                  </select>
                ) : null}
                <input className={inputCls} placeholder={t('pro.displayName', { defaultValue: 'Display name (optional)' })} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                <input className={inputCls} placeholder={t('pro.licenseNumber', { defaultValue: 'Licence number (to verify now)' })} value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
              </div>
              <button
                onClick={addRole}
                disabled={!addType || busy}
                className="mt-4 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#004D40] text-white font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {t('pro.addRoleBtn', { defaultValue: 'Add role' })}
              </button>
            </section>

            {/* Register a company */}
            {!hasCompany ? (
              <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
                <h2 className="font-semibold text-[var(--pv-color-primary)] dark:text-gray-100 mb-1 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-violet-600" /> {t('pro.registerCompany', { defaultValue: 'Register a company' })}
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  {t('pro.registerCompanyHint', { defaultValue: 'Create a company you own. You can then add the firm’s licensed activities (brokerage, development, etc.).' })}
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className={inputCls} placeholder={t('pro.companyName', { defaultValue: 'Company name' })} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                  <input className={inputCls} placeholder={t('pro.companyPhone', { defaultValue: 'Company phone (optional)' })} value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} />
                </div>
                <button
                  onClick={registerCompany}
                  disabled={!companyName || busy}
                  className="mt-4 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#004D40] text-white font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
                  {t('pro.registerCompanyBtn', { defaultValue: 'Register company' })}
                </button>
              </section>
            ) : null}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
